/**
 * =============================================================================
 * Promotion i18n (SK + EN) — FE issue #23
 * =============================================================================
 *
 * The admin portal has no global i18n framework yet; per the platform-wide
 * mandate every NEW user-facing text ships in Slovak and English. This tiny
 * helper resolves the UI language (stored preference, else browser locale)
 * and serves the promote/demote strings in both languages.
 */

export type Lang = 'sk' | 'en'

const STORAGE_KEY = 'admin_portal_lang'

/** Resolve UI language: stored preference > browser locale > 'en'. */
export function getLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'sk' || stored === 'en') return stored
  } catch {
    // storage unavailable (private mode etc.) — fall through to locale
  }
  const locale = (typeof navigator !== 'undefined' && navigator.language) || ''
  return locale.toLowerCase().startsWith('sk') ? 'sk' : 'en'
}

interface BiText {
  sk: string
  en: string
}

const TEXTS = {
  promoteAction: { sk: 'Presunúť do produkcie', en: 'Move to production' },
  demoteAction: { sk: 'Vrátiť do dev', en: 'Return to dev' },
  promoteTitle: { sk: 'Presunúť do produkcie', en: 'Move to production' },
  demoteTitle: { sk: 'Vrátiť z produkcie do dev', en: 'Return from production to dev' },
  promoteWarnTitle: { sk: 'Appka pôjde na reálne zariadenia', en: 'App will reach real devices' },
  promoteWarnBody: {
    sk: 'Zariadenia v normálnom režime dostávajú výhradne produkčné appky. Pred presunom sa overí: schválenie, zhoda digestov, existencia image v Harbore a priradený typ zariadenia.',
    en: 'Devices in normal mode only receive production apps. Before the move we validate: approval, digest match, image existence in Harbor, and an assigned device type.',
  },
  demoteWarnTitle: { sk: 'Appka zmizne zo zariadení', en: 'App will disappear from devices' },
  demoteWarnBody: {
    sk: 'Appka okamžite prestane byť dostupná pre zariadenia v normálnom režime.',
    en: 'The app immediately stops being delivered to devices in normal mode.',
  },
  commentLabel: { sk: 'Komentár (voliteľný)', en: 'Comment (optional)' },
  commentPlaceholder: { sk: 'Poznámka k tejto akcii…', en: 'A note about this action…' },
  cancel: { sk: 'Zrušiť', en: 'Cancel' },
  confirmPromote: { sk: 'Presunúť do produkcie', en: 'Move to production' },
  confirmDemote: { sk: 'Vrátiť do dev', en: 'Return to dev' },
  working: { sk: 'Prebieha…', en: 'Working…' },
  checksTitle: { sk: 'Výsledok validácie', en: 'Validation result' },
  promotedOk: { sk: 'Appka bola presunutá do produkcie.', en: 'App was moved to production.' },
  demotedOk: { sk: 'Appka bola vrátená do dev.', en: 'App was returned to dev.' },
  blocked: { sk: 'Akcia bola zablokovaná validáciou.', en: 'The action was blocked by validation.' },
  requestFailed: { sk: 'Požiadavka zlyhala.', en: 'Request failed.' },
  close: { sk: 'Zavrieť', en: 'Close' },

  // Auto-approve of dev apps (issue #50) — explains the *absence* of the usual
  // "pending re-approval" banner so it does not read as a bug.
  autoApproveBadge: { sk: 'Automatické schvaľovanie', en: 'Auto-approved' },
  autoApproveTitle: {
    sk: 'Nové buildy sa schvaľujú automaticky',
    en: 'New builds are approved automatically',
  },
  autoApproveBody: {
    sk: 'Toto je vývojová (dev) appka — vidia ju výhradne zariadenia v režime dev_mode. Po každom novom builde sa schválený digest posunie automaticky, appka ostáva v stave „approved“ a zo zariadení nezmizne. Ručné schvaľovanie preto netreba; každé automatické schválenie je zapísané v audite ako app.auto_approve.',
    en: 'This is a development (dev) app — only devices in dev_mode ever see it. After every new build the approved digest advances automatically, the app stays "approved" and never disappears from devices. No manual approval is needed; every automatic release is recorded in the audit trail as app.auto_approve.',
  },
  autoApproveProdNote: {
    sk: 'Appky v prostredí stage a prod sa naďalej schvaľujú ručne.',
    en: 'Apps in the stage and prod environments still require a manual approval.',
  },
  autoApproveDisableHint: {
    sk: 'Vypnúť sa dá nastavením AUTO_APPROVE_DEV_APPS=false na admin portal API.',
    en: 'It can be turned off with AUTO_APPROVE_DEV_APPS=false on the admin portal API.',
  },
} satisfies Record<string, BiText>

export type PromotionTextKey = keyof typeof TEXTS

/** Translate one of the promotion UI strings into the active language. */
export function pt(key: PromotionTextKey, lang: Lang = getLang()): string {
  return TEXTS[key][lang]
}

/** Pick the right language variant of a BE promotion-check message. */
export function checkMessage(
  check: { message_en: string; message_sk: string },
  lang: Lang = getLang()
): string {
  return lang === 'sk' ? check.message_sk : check.message_en
}
